import React, { FC, useCallback, useMemo } from 'react';

import { ScoringRequestType } from 'types/transactionTypes';

import { ReactComponent as CopyIcon } from 'assets/icons/copy-icon-outlined.svg';

import s from './ScoringRequestDetails.module.scss';

interface ScoringRequestDetailsProps {
  request: ScoringRequestType;
}

type SignalEntry = [string, { color: string; value: number }];

const formatNumber = (value: number | null | undefined, decimals = 2): string => {
  if (value == null) {
    return '—';
  }
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

const ScoringRequestDetails: FC<ScoringRequestDetailsProps> = ({ request }) => {
  const signalEntries = useMemo<SignalEntry[]>(() => {
    const raw = request.signals ? Object.entries(request.signals) : [];
    return raw
      .filter(
        (entry): entry is SignalEntry =>
          !!entry[1] && typeof entry[1].value === 'number' && entry[1].value > 0,
      )
      .sort((a, b) => b[1].value - a[1].value);
  }, [request.signals]);

  const companies = request.company_full_names ?? request.company_names ?? [];
  const companyTypes = request.company_types ?? [];
  const address = request.address?.address ?? '';

  const handleCopy = useCallback(() => {
    if (address) {
      navigator.clipboard.writeText(address).catch(() => {
        /* clipboard API can fail in insecure contexts */
      });
    }
  }, [address]);

  const cryptoAmount =
    request.asset_amount != null
      ? `${formatNumber(request.asset_amount, request.asset_decimals ?? 2)} ${request.asset ?? ''}`
      : '—';

  const transactions = useMemo(() => {
    const hashes = request.transaction_hashes ?? [];
    const scans = request.scan_refs ?? [];
    const usdAmounts = request.transaction_usd_amounts ?? [];
    const assetAmounts = request.transaction_asset_amounts ?? [];
    const types = request.transaction_types ?? [];
    const length = Math.max(
      hashes.length,
      scans.length,
      usdAmounts.length,
      assetAmounts.length,
      types.length,
    );
    return Array.from({ length }, (_, i) => ({
      hash: hashes[i],
      scanRef: scans[i],
      usdAmount: usdAmounts[i],
      assetAmount: assetAmounts[i],
      type: types[i],
    }));
  }, [
    request.transaction_hashes,
    request.scan_refs,
    request.transaction_usd_amounts,
    request.transaction_asset_amounts,
    request.transaction_types,
  ]);

  return (
    <div className={s.wrapper}>
      <div className={s.column}>
        <div className={s.section}>
          <span className={s.sectionTitle}>Risk signals</span>
          {signalEntries.length === 0 ? (
            <span className={s.empty}>No signals</span>
          ) : (
            <div className={s.signalList}>
              {signalEntries.map(([name, signal]) => {
                const percent = signal.value * 100;
                return (
                  <div className={s.signalRow} key={name}>
                    <div className={s.signalLabel}>
                      <span className={s.signalName}>{name.split('_').join(' ')}</span>
                      <span className={s.signalPercent}>{formatNumber(percent, 1)}%</span>
                    </div>
                    <div className={s.signalBarWrapper}>
                      <div
                        className={s.signalBar}
                        style={
                          {
                            '--signal-width': `${Math.min(percent, 100)}%`,
                            '--signal-color': signal.color,
                          } as React.CSSProperties
                        }
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div className={s.column}>
        <div className={s.section}>
          <span className={s.sectionTitle}>Details</span>
          <div className={s.metaList}>
            <div className={s.metaRow}>
              <span className={s.metaKey}>Crypto amount</span>
              <span className={s.metaValue}>{cryptoAmount}</span>
            </div>
            {companies.length > 0 && (
              <div className={s.metaRow}>
                <span className={s.metaKey}>Companies</span>
                <span className={s.metaValue}>{companies.join(', ')}</span>
              </div>
            )}
            {companyTypes.length > 0 && (
              <div className={s.metaRow}>
                <span className={s.metaKey}>Company type</span>
                <span className={s.metaValue}>{companyTypes.join(', ')}</span>
              </div>
            )}
          </div>
        </div>

        {transactions.length > 0 && (
          <div className={s.section}>
            <span className={s.sectionTitle}>
              Transactions {transactions.length > 1 ? `(${transactions.length})` : ''}
            </span>
            <div className={s.transactionList}>
              {transactions.map((tx, index) => (
                <div className={s.transactionItem} key={tx.hash ?? tx.scanRef ?? index}>
                  <div className={s.metaRow}>
                    {tx.type && (
                      <span className={s.transactionType}>{tx.type.replace(/_/g, ' ')}</span>
                    )}
                    {tx.scanRef ? (
                      <a
                        className={s.explorerLink}
                        href={tx.scanRef}
                        target="_blank"
                        rel="noreferrer noopener">
                        View ↗
                      </a>
                    ) : (
                      <span className={s.empty}>—</span>
                    )}
                  </div>
                  {(tx.usdAmount != null || tx.assetAmount != null) && (
                    <div className={s.metaRow}>
                      <span className={s.metaKey}>
                        {tx.assetAmount != null
                          ? `${formatNumber(tx.assetAmount, request.asset_decimals ?? 2)} ${
                              request.asset ?? ''
                            }`
                          : '—'}
                      </span>
                      <span className={s.metaValue}>
                        {tx.usdAmount != null ? `${formatNumber(tx.usdAmount, 2)} $` : '—'}
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {address && (
          <div className={s.section}>
            <span className={s.sectionTitle}>Address</span>
            <div className={s.addressBox}>
              <span className={s.addressText}>{address}</span>
              <button
                type="button"
                className={s.copyButton}
                onClick={handleCopy}
                aria-label="Copy address">
                <CopyIcon />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScoringRequestDetails;
